import { ReactNode, RefObject } from "react";
import { useTranslation } from "react-i18next";
import LeafTile from "../LeafTile/LeafTile";
import "./LeafTileValue.scss";
import LeafRectangleLoader from "../LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { LeafIconButton } from "../LeafIconButton/LeafIconButton";
import { IconTable } from "../../icons/IconTable/IconTable";
import defaultAwardContent from "../../../content/awardLabels";

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  id: string;
  title: string;
  value: string;
  isLoading: boolean;
  icon?: ReactNode;
  buttonAriaLabel?: string;
  symbolIcon?: ReactNode;
  callback?: Function;
  buttonRef?: RefObject<HTMLButtonElement | null>;
  hideDesktopIcon?: boolean;
  hideMobileIcon?: boolean;
  valueClass?: string;
  mobileInlineIcon?: boolean;
}

const LeafTileValue = ({
  id,
  title,
  value,
  isLoading,
  icon,
  buttonAriaLabel = "",
  symbolIcon,
  callback,
  buttonRef,
  hideMobileIcon = false,
  hideDesktopIcon = false,
  valueClass = "",
  mobileInlineIcon = false,
}: Props) => {
  const { t } = useTranslation(["awardLabels"]);
  const noDataLabel = t(
    "awardLabels.no-data-available",
    defaultAwardContent.awardLabels["no-data-available"] as string,
  );
  const isNoData = value === "--";
  const renderButton = () => {
    if (icon && callback && !isLoading) {
      return (
        <span
          className={`LeafTileValue__button LeafTileValue__button${
            hideMobileIcon === true ? "--hide-mobile" : ""
          } LeafTileValue__button${hideDesktopIcon === true ? "--hide-desktop" : ""}`}
        >
          <LeafIconButton
            Icon={icon}
            className="LeafTileValue__button-icon"
            role="presentation"
            onClick={(e) => {
              e.preventDefault();
              callback();
            }}
            tabIndex={0}
            aria-label={buttonAriaLabel || "Table Icon Button"}
          />
        </span>
      );
    }

    return <></>;
  };
  return (
    <LeafTile id={id} isLoading={false}>
      <div className="LeafTileValue">
        <span className="LeafTileValue__symbol">{symbolIcon ? symbolIcon : <></>}</span>
        <span className="LeafTileValue__content-container">
          <LeafRectangleLoader isLoading={isLoading} height="3.375rem" width="100%">
            <p className="LeafTileValue__title">
              {title}
              {mobileInlineIcon && icon && callback && !isLoading && (
                <span className="LeafTileValue__inline-icon">
                  <LeafIconButton
                    Icon={icon}
                    className="LeafTileValue__button-icon"
                    role="presentation"
                    onClick={(e) => {
                      e.preventDefault();
                      callback();
                    }}
                    tabIndex={0}
                    aria-label={buttonAriaLabel || "Table Icon Button"}
                  />
                </span>
              )}
            </p>
            <p
              className={"LeafTileValue__value " + valueClass}
              {...(isNoData ? { role: "text", "aria-label": noDataLabel } : {})}
            >
              {isNoData ? <span aria-hidden="true">{value}</span> : value}
            </p>
          </LeafRectangleLoader>
        </span>
        {renderButton()}
      </div>
    </LeafTile>
  );
};

export default LeafTileValue;
